import React, { useMemo } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import { Line } from "react-chartjs-2";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

export const UtilizationChart = ({ hourlyUtilizationData, floorCapacity, loading }) => {
  const chartData = useMemo(() => {
    if (!hourlyUtilizationData || hourlyUtilizationData.length === 0) {
      return null;
    }

    const labels = hourlyUtilizationData.map((entry) => {
      const hour = entry.hour % 12 || 12;
      const period = entry.hour >= 12 ? "PM" : "AM";
      return `${hour}:00 ${period}`;
    });

    const counts = hourlyUtilizationData.map((entry) => entry.count);

    return {
      labels,
      datasets: [
        {
          label: "Hourly Utilization",
          data: counts,
          fill: true,
          backgroundColor: "rgba(75, 192, 192, 0.2)",
          borderColor: "#dc3545",
          tension: 0.4,
          pointRadius: 4,
          pointHoverRadius: 6,
          pointBackgroundColor: "#dc3545",
          pointBorderColor: "#fff",
          pointBorderWidth: 2,
        },
      ],
    };
  }, [hourlyUtilizationData]);

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: "top",
          labels: {
            font: {
              family: "'Inter', sans-serif",
            },
          },
        },
        tooltip: {
          callbacks: {
            label: (context) => {
              const value = context.raw;
              const percentage = ((value / floorCapacity) * 100).toFixed(1);
              return `Utilization: ${value} / ${floorCapacity} (${percentage}%)`;
            },
          },
        },
      },
      scales: {
        y: {
          beginAtZero: true,
          title: {
            display: true,
            text: "Utilization Count",
            font: {
              family: "'Inter', sans-serif",
              size: 12,
            },
          },
          grid: {
            color: (context) => {
              if (context.tick.value === floorCapacity) {
                return "rgba(220, 53, 69, 0.5)";
              }
              return "rgba(0, 0, 0, 0.1)";
            },
            lineWidth: (context) => {
              if (context.tick.value === floorCapacity) {
                return 2;
              }
              return 1;
            },
          },
          ticks: {
            font: {
              family: "'Inter', sans-serif",
            },
          },
        },
        x: {
          title: {
            display: true,
            text: "Hour of Day",
            font: {
              family: "'Inter', sans-serif",
              size: 12,
            },
          },
          grid: {
            display: false,
          },
          ticks: {
            font: {
              family: "'Inter', sans-serif",
            },
          },
        },
      },
    }),
    [floorCapacity]
  );

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (!chartData) {
    return (
      <div className="alert alert-warning">
        No utilization data available for the selected filters.
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="card-title mb-0">Hourly Utilization Chart</h3>
        <span className="badge bg-info">Capacity: {floorCapacity}</span>
      </div>
      <div style={{ height: "400px", position: "relative" }}>
        <Line data={chartData} options={chartOptions} />
      </div>
    </div>
  );
};
